import React from 'react';

interface SpecificationItem {
    heading: string;
    description?: string;
    featuresHeading?: string;
    features: (string | { text: string; featureDescription?: string })[];
    additionalText?: string;
    image?: string;
    imageAlt?: string;
    colors?: string;
    packaging?: string;
}

interface SpecificationsProps {
    title: string;
    items: SpecificationItem[];
    className?: string;
}

const SpecificationSection: React.FC<{ item: SpecificationItem }> = ({ item }) => {
    if (item.image) {
        // Image-based specification (like masks)
        return (
            <div className="specification-text-img flex flex-col lg:flex-row items-start gap-8 mb-8 pb-8 border-b border-gray-200" style={{ marginBottom: '2rem' }}>
                {/* Image - First on Mobile, Right on Desktop */}
                <div className="specification-img order-first lg:order-last">
                    <img 
                        src={item.image} 
                        alt={item.imageAlt || item.heading}
                        loading="lazy"
                    />
                </div>
                
                {/* Text Content - Below Image on Mobile, Left on Desktop */}
                <div className="specification-text flex-1 order-last lg:order-first">
                    <h3 className="text-xl font-medium text-gray-800 mb-4">
                        {item.heading}
                    </h3>
                    {item.description && (
                        <p className="text-base text-gray-600 mb-4 font-normal">
                            {item.description}
                        </p>
                    )}
                    {item.featuresHeading && (
                        <h4 className="text-lg font-medium text-gray-800 mb-3">
                            {item.featuresHeading}
                        </h4>
                    )}
                    <ul className="dot-list-st text-base text-gray-600 space-y-2 mb-4 font-medium">
                        {item.features.map((feature, index) => (
                            <React.Fragment key={index}>
                                <li className="flex items-start">
                                    <span>{typeof feature === 'string' ? feature : feature.text}</span>
                                </li>
                                {typeof feature === 'object' && feature.featureDescription && (
                                    <p className="feature-description-italic">
                                        {feature.featureDescription}
                                    </p>
                                )}
                            </React.Fragment>
                        ))}
                    </ul>
                    {item.additionalText && (
                        <p className="text-base text-gray-600 mb-4 font-normal" style={{ fontStyle: 'italic' }}>
                            {item.additionalText}
                        </p>
                    )}
                </div>
            </div>
        );
    } else {
        // Text-only specification (like surface wipes, hand sanitizer)
        return (
            <div className="mb-8" style={{ marginBottom: '2rem' }}>
                <h3 className="text-xl font-medium text-gray-800 mb-4">
                    {item.heading}
                </h3>
                {item.description && (
                    <p className="text-base text-gray-600 mb-4 font-normal">
                        {item.description}
                    </p>
                )}
                {item.featuresHeading && (
                    <h4 className="text-lg font-medium text-gray-800 mb-3">
                        {item.featuresHeading}
                    </h4>
                )}
                <ul className="dot-list-st text-base text-gray-600 space-y-2 font-medium">
                    {item.features.map((feature, index) => (
                        <React.Fragment key={index}>
                            <li className="flex items-start">
                                <span>{typeof feature === 'string' ? feature : feature.text}</span>
                            </li>
                            {typeof feature === 'object' && feature.featureDescription && (
                                <p className="feature-description-italic">
                                    {feature.featureDescription}
                                </p>
                            )}
                        </React.Fragment>
                    ))}
                </ul>
                {item.additionalText && (
                    <p className="text-base text-gray-600 mb-4 font-normal" style={{ fontStyle: 'italic' }}>
                        {item.additionalText}
                    </p>
                )}
            </div>
        );
    }
};

const Specifications: React.FC<SpecificationsProps> = ({ title, items, className = '' }) => {
    return (
        <div className={`w-full py-4 bg-white ${className}`}>
            <div className="px-5 ml-0 md:ml-[3%] specifications-area pl-5">
                <div>
                    <div className="specification-seciton">
                        <h2>{title}</h2>
                        <div className="specification-details">
                            {items.map((item, index) => (
                                <SpecificationSection key={index} item={item} />
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Specifications; 